import { v4 as uuidv4 } from 'uuid'
import { mountHeaders } from '@/services/api'
import getPointIdFromStorage from '@/services/getPointId'

export const SESSION_EVENTS = Object.freeze({
  // Session management
  START_SESSION: 'start_session',
  RESTART_SESSION: 'restart_session',
  CLOSE_SESSION: 'close_session',

  // Authentication & Profile
  LOGIN_ACTIVATE_MAIL: 'login_activate_mail',
  SEND_ACTIVATION_MAIL: 'send_activation_mail',
  LOGIN_SOCIAL: 'login_social',
  HISTORY: 'history',

  // Transaction flow
  TAKE_BATTERY: 'take_battery',
  TAKE_BATTERY_SUCCESS: 'take_battery_success',
  TAKE_BATTERY_ERROR: 'take_battery_error',
  TRY_AGAIN_INITIATED: 'try_again_initiated',
  REFUND_INITIATED: 'refund_initiated',
  VALID_TOKEN: 'valid_token',
  TOKEN_SELECTION: 'token_selection',
  TOKEN_REDIRECT: 'token_redirect',
  NO_BATTERY_WITH_CABLE: 'no_battery_with_cable',
  CABLE_TYPE_SELECTION: 'cable_type_selection',
  EVALUATED_TRANSACTION: 'evaluated_transaction',

  // Payment & Registration
  PAYMENT_METHOD: 'payment_method',
  CREDIT_CARD_REGISTRATION: 'credit_card_registration',
  CREDIT_CARD_REGISTRATION_INLINE: 'credit_card_registration_inline',
  PIX_REGISTRATION: 'pix_registration',
  PIX_REGISTRATION_INLINE: 'pix_registration_inline',
  PIX_PAYMENT_CANCELED: 'pix_payment_canceled',
  PIX_PAYMENT_EXPIRED: 'pix_payment_expired',
  PIX_QRCODE_GENERATED: 'pix_qrcode_generated',

  // Navigation & UI
  OPEN_PRICE_TABLE: 'open_price_table',
  BACK_BUTTON: 'back_button',

  // Map & Location
  MAP_INITIALIZED: 'map_initialized',
  MAP_INITIALIZATION_FAILED: 'map_initialization_failed',
  MAP_LOCATION_PERMISSION_REQUESTED: 'map_location_permission_requested',
  MAP_LOCATION_PERMISSION_GRANTED: 'map_location_permission_granted',
  MAP_LOCATION_PERMISSION_DENIED: 'map_location_permission_denied',
  MAP_LOCATION_PERMISSION_SKIPPED: 'map_location_permission_skipped',
  MAP_LOCATION_RETRY: 'map_location_retry',

  // Map Interaction
  MAP_MARKER_CLICKED: 'map_marker_clicked',
  MAP_POINT_DETAILS_OPENED: 'map_point_details_opened',
  MAP_POINT_DETAILS_CLOSED: 'map_point_details_closed',
  MAP_POINT_SELECTED: 'map_point_selected',
  MAP_CHECK_PRICES_CLICKED: 'map_check_prices_clicked',
  MAP_DISTANCE_WARNING_SHOWN: 'map_distance_warning_shown',
  MAP_DISTANCE_WARNING_ACCEPTED: 'map_distance_warning_accepted',
  MAP_DISTANCE_WARNING_REJECTED: 'map_distance_warning_rejected',

  // Manual Token & QR
  MAP_MANUAL_INPUT_OPENED: 'map_manual_input_opened',
  MAP_MANUAL_INPUT_MINIMIZED: 'map_manual_input_minimized',
  MAP_MANUAL_TOKEN_VALIDATED: 'map_manual_token_validated',
  MAP_MANUAL_TOKEN_INVALID: 'map_manual_token_invalid',
  MAP_QR_INITIALIZATION_SUCCESS: 'map_qr_initialization_success',
  MAP_QR_INITIALIZATION_FAILED: 'map_qr_initialization_failed',
  MAP_QR_POINT_OPENED: 'map_qr_point_opened',

  // External Links
  LIGHTCOM_LINK_OPENED: 'lightcom_link_opened',

  // Map Validation & Errors
  MAP_VALIDATION_ERROR: 'map_validation_error',
  MAP_VALIDATION_RETRY: 'map_validation_retry',
  MAP_NEARBY_TERMINAL_SELECTED: 'map_nearby_terminal_selected',
  MAP_CABLE_SELECTED_FROM_ERROR: 'map_cable_selected_from_error',

  // Checkout Flow
  CHECKOUT_FLOW_OPENED: 'checkout_flow_opened',
  CHECKOUT_FLOW_CLOSED: 'checkout_flow_closed',
  CHECKOUT_FLOW_STEP_CHANGED: 'checkout_flow_step_changed',
  CHECKOUT_FLOW_CABLE_SELECTED: 'checkout_flow_cable_selected',
  CHECKOUT_FLOW_PAYMENT_CHECK: 'checkout_flow_payment_check',
  CHECKOUT_FLOW_VALIDATION_STARTED: 'checkout_flow_validation_started',
  CHECKOUT_FLOW_VALIDATION_SUCCESS: 'checkout_flow_validation_success',
  CHECKOUT_FLOW_VALIDATION_ERROR: 'checkout_flow_validation_error',
  CHECKOUT_FLOW_PAYMENT_SUCCESS: 'checkout_flow_payment_success',
  CHECKOUT_FLOW_PAYMENT_ERROR: 'checkout_flow_payment_error',
  CHECKOUT_FLOW_CANCELED: 'checkout_flow_canceled'
})

class ClickTracker {
  constructor() {
    this.clickEvents = []
    this.handleClick = this.handleClick.bind(this)
  }

  start() {
    document.addEventListener('click', this.handleClick)
  }

  stop() {
    document.removeEventListener('click', this.handleClick)
  }

  handleClick(event) {
    const target = event.target
    const parentElement = target.parentElement
    let text = target.textContent.trim()

    if (target.tagName === 'input') {
      text = '***'
    }

    let id = target.id || null
    if (id !== '' && id !== null) {
      id = '#' + id
    }

    const clickData = {
      id: id,
      text: text,
      tag: `<${target.tagName.toLowerCase()}>`,
      parent: ClickTracker.getElementText(parentElement, text),
      datetime: new Date().toISOString(),
      position: {
        x: event.clientX,
        y: event.clientY
      }
    }

    this.clickEvents.push(clickData)
  }

  static getElementText(element, compareText) {
    let currentElement = element
    let depth = 0
    while (currentElement && depth < 10) {
      const dataText = currentElement.getAttribute('data-text') || currentElement.textContent.trim()
      if (dataText !== '' && dataText !== compareText) {
        return {
          text: dataText.trim(),
          tag: `<${currentElement.tagName.toLowerCase()}>`,
          depth: depth
        }
      }
      currentElement = currentElement.parentElement
      depth++
    }
    return ''
  }

  getClicks() {
    return this.clickEvents
  }

  clearClicks() {
    this.clickEvents = []
  }
}

class SessionRecorder {
  constructor() {
    if (!SessionRecorder.instance) {
      this.sessionId = null
      this.tracker = new ClickTracker()
      this.sendInterval = null
      this.userEvents = []
      this.errors = []
      this.hasClosedSessionEvent = false
      this.stopRecording = null
      SessionRecorder.instance = this
    }
    return SessionRecorder.instance
  }

  // ============ Session Lifecycle ============

  startSession() {
    this.sessionId = this.sessionId || sessionStorage.getItem('session_id') || uuidv4()
    sessionStorage.setItem('session_id', this.sessionId)
    console.log('Session ID:', this.sessionId)
  }

  startRecording() {
    const objRef = this
    this.tracker.start()
    this.sendInterval = setInterval(() => this.sendEventToBackend(), 3000)

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        objRef.handleUnloadPage()
      }
    })

    window.addEventListener('beforeunload', () => {
      this.handleUnloadPage()
    })

    window.addEventListener('unload', () => {
      this.handleUnloadPage()
    })

    this.sendEventToBackend()
  }

  stopRecordingSession() {
    if (this.stopRecording) {
      this.tracker.stop()
      this.stopRecording = null
    }
    this.sessionId = null
    sessionStorage.removeItem('session_id')
    clearInterval(this.sendInterval)
  }

  handleUnloadPage() {
    if (!this.hasClosedSessionEvent) {
      this.hasClosedSessionEvent = true
      this.addUserEvent(new UserEvent(SESSION_EVENTS.CLOSE_SESSION))
    }
    this.sendEventToBackend()
  }

  // ============ Event Management ============

  addUserEvent(userEvent) {
    this.userEvents.push(userEvent)
  }

  addError(error) {
    this.errors.push(error)
  }

  addErrorFromQueue() {
    const errorQueue = JSON.parse(localStorage.getItem('errorQueue') || '[]')
    if (errorQueue.length === 0) return

    this.errors.push(...errorQueue)
  }

  // ============ Backend Integration ============

  sendEventToBackend() {
    try {
      this.addErrorFromQueue()

      const payload = {
        session_id: this.sessionId,
        click_events: this.tracker.getClicks(),
        events: this.userEvents,
        errors: this.errors
      }

      if (payload.click_events.length === 0 && payload.events.length === 0 && payload.errors.length === 0) {
        return
      }

      if (SessionRecorder.sendSessionData(payload)) {
        localStorage.removeItem('errorQueue')
      }

      this.tracker.clearClicks()
      this.userEvents = []
      this.errors = []
      return this
    } catch (error) {
      console.error('Error preparing session data:', error)
    }
  }

  static sendSessionData(payload) {
    let config = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8'
      }
    }
    config = mountHeaders(config)
    const baseURL = import.meta.env.VITE_MOBILE_API

    Object.keys(config.headers).forEach(key => {
      if (payload.hasOwnProperty(key)) {
        delete config.headers[key]
      }
    })

    payload = {
      ...payload,
      ...config.headers
    }

    try {
      const blob = new Blob([JSON.stringify(payload)])
      if (navigator.sendBeacon?.(`${baseURL}customer/session`, blob)) {
        return true
      }
    } catch (e) {
      console.warn('Falha no Beacon', e)
      return false
    }
  }
}

class UserEvent {
  constructor(event, transaction_id = null, extra = null) {
    const point = getPointIdFromStorage()

    this.event = event
    this.datetime = new Date().toISOString()

    // Only include IDs if they are provided or found in storage
    this.transaction_id = transaction_id
    this.point_id = point?.id || null

    this.extra = {
      type_token_selection: point?.type || null
    }

    if (extra !== null) {
      this.extra = { ...this.extra, ...extra }
    }
  }
}

const sessionRecorder = new SessionRecorder()

export { sessionRecorder, UserEvent }
